import L from 'leaflet'
// Must come after 'leaflet': the plugin extends the global L that Leaflet sets on window.
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import './markerCluster.css'

/**
 * Crowded pincodes (dozens of learners at one centroid) turned into one unclickable blob, so the
 * participant dots go through a cluster group. Clicking a bubble zooms to its members; at max zoom
 * the ones still sharing a spot fan out (spiderfy); past z17 everything is drawn individually.
 */
export function createDotClusterGroup(): L.MarkerClusterGroup {
  return L.markerClusterGroup({
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    maxClusterRadius: 45,
    disableClusteringAtZoom: 17,
    zoomToBoundsOnClick: true,
    chunkedLoading: true,
    iconCreateFunction: (cluster) => {
      const count = cluster.getChildCount()
      const [size, variant] = count < 10 ? [30, 'sm'] : count < 50 ? [36, 'md'] : [44, 'lg']
      return L.divIcon({
        html: `<div class="yd-cluster yd-cluster--${variant}">${count}</div>`,
        className: 'yd-cluster-icon',
        iconSize: [size, size],
      })
    },
  })
}

/**
 * The divIcon equivalent of the old `L.circleMarker({ radius, weight, color, fillColor,
 * fillOpacity })`: an SVG stroke straddles the circle's edge, so the outer diameter is
 * 2*radius + weight and a border-box div with a `weight`-px border reproduces it exactly.
 * Anchored at its centre, so tooltip offsets behave as they did for the circle marker.
 */
export function dotIcon({
  radius,
  weight,
  color,
  fillColor,
  fillOpacity,
}: {
  radius: number
  weight: number
  color: string
  fillColor: string
  fillOpacity: number
}): L.DivIcon {
  const size = 2 * radius + weight
  return L.divIcon({
    html: `<span class="yd-dot" style="border:${weight}px solid ${color};background:${withAlpha(fillColor, fillOpacity)};"></span>`,
    className: 'yd-dot-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

/** '#f59e0b' + 0.95 -> 'rgba(245,158,11,0.95)'. Only ever fed our own six-digit hex constants. */
function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}
